// biome-ignore-all lint/performance/noBarrelFile: Package entrypoint intentionally re-exports OpenClaw runtime helpers.
import { bridgeAgentControllerConversationRef, buildAdapterAgentControllerConversationRef, buildAdapterCancelRequest, buildExecutableLaunchContract, createSharedControlShell, createSharedDispatchShell, resolveBuiltinAdapterConversationBridgeSpec, } from "@wakeloop/adapter-core";
import { resolveOpenClawAgentId as resolveOpenClawAgentIdLocal, resolveOpenClawTimeoutMs as resolveOpenClawTimeoutMsLocal, verifyOpenClawLaunchContract as verifyOpenClawLaunchContractLocal, } from "./config/openclaw-runtime-config.js";
import { invokeOpenClawWithSingleContextHeal as invokeOpenClawWithSingleContextHealLocal } from "./invoke/openclaw-invoke.js";
import { invokeOpenClawGatewayTransport } from "./transport/openclaw-gateway-transport.js";
export { resolveInstalledOpenClawLaunchCandidates, resolveInstalledOpenClawLaunchContract, resolveOpenClawAgentId, resolveOpenClawBin, resolveOpenClawBinCandidates, resolveOpenClawTimeoutMs, verifyOpenClawLaunchContract, } from "./config/openclaw-runtime-config.js";
export { isLikelyOpenClawContextLostError } from "./context/openclaw-context-heal.js";
export { invokeOpenClawWithSingleContextHeal, OpenClawRuntimeError, } from "./invoke/openclaw-invoke.js";
export { parseOpenClawOutputLine } from "./output/openclaw-output-parser.js";
export { invokeOpenClawCliTransport, OpenClawCliTransportError, } from "./transport/openclaw-cli-transport.js";
export { invokeOpenClawGatewayTransport, OpenClawGatewayTransportError, } from "./transport/openclaw-gateway-transport.js";
const OPENCLAW_CONVERSATION_BRIDGE_SPEC = resolveBuiltinAdapterConversationBridgeSpec("openclaw");
const resolveOpenClawSessionConversation = (agentControllerConversation) => {
    if (!agentControllerConversation) {
        return null;
    }
    const agentControllerConversationId = normalizeOptionalText(agentControllerConversation.agentControllerConversationId);
    if (!agentControllerConversationId) {
        throw new Error("conversation.bridge.agent_controller_conversation_id_missing: expected builtin:openclaw session agentControllerConversationId");
    }
    if (agentControllerConversation.agentControllerRef !== "builtin:openclaw" ||
        agentControllerConversation.agentControllerConversationKind !== "session") {
        throw new Error(`conversation.bridge.agent_controller_conversation_mismatch: expected builtin:openclaw session, received ${agentControllerConversation.agentControllerRef}/${agentControllerConversation.agentControllerConversationKind}`);
    }
    return buildAdapterAgentControllerConversationRef({
        agentControllerRef: "builtin:openclaw",
        agentControllerConversationId,
        agentControllerConversationKind: "session",
    });
};
const resolveOpenClawInvokeRequest = (input) => {
    const agentControllerConversation = resolveOpenClawSessionConversation(input.request.agentControllerConversation);
    return {
        ...input.request,
        agentControllerConversationId: normalizeOptionalText(input.request.agentControllerConversationId),
        ...(agentControllerConversation ? { agentControllerConversation } : {}),
    };
};
const resolveOpenClawContextDelta = (input) => {
    if (!input.result.contextDelta) {
        return input.result.contextDelta;
    }
    if (input.result.contextDelta.agentControllerConversation) {
        return input.result.contextDelta;
    }
    const continuationConversation = resolveOpenClawSessionConversation(input.invokeRequest.agentControllerConversation);
    const canonicalConversation = continuationConversation ??
        (OPENCLAW_CONVERSATION_BRIDGE_SPEC
            ? bridgeAgentControllerConversationRef({
                bridgeSpec: OPENCLAW_CONVERSATION_BRIDGE_SPEC,
                agentControllerConversationId: input.result.contextDelta.agentControllerConversationId,
                agentControllerConversation: input.result.contextDelta.agentControllerConversation,
            })
            : null);
    return {
        ...input.result.contextDelta,
        ...(canonicalConversation
            ? { agentControllerConversation: canonicalConversation }
            : {}),
    };
};
export const createOpenClawAdapter = (options) => {
    const controlShell = createSharedControlShell({
        gracefulTimeoutMs: options.gracefulTimeoutMs,
        sendCancel: options.sendCancel,
        sendInterrupt: options.sendInterrupt,
        waitForMs: options.waitForMs,
    });
    const dispatch = createSharedDispatchShell({
        adapterId: "openclaw",
        bridgeSpec: OPENCLAW_CONVERSATION_BRIDGE_SPEC,
        invoke: options.invoke,
        invokeByMode: options.invokeByMode,
        resolveDefaultInvoke: () => resolveDefaultOpenClawInvoke(options),
        resolveInvokeRequest: ({ request }) => resolveOpenClawInvokeRequest({ request }),
        resolveSuccessContextDelta: ({ invokeRequest, result }) => resolveOpenClawContextDelta({
            invokeRequest,
            result,
        }),
        runtimeOptions: options,
    });
    return {
        adapterId: "openclaw",
        cancel: controlShell.cancel,
        dispatch,
        interrupt: controlShell.interrupt,
    };
};
export const buildOpenClawInterruptRequest = (input) => ({
    mode: "graceful",
    reason: input.reason,
    taskId: input.taskId,
});
export const buildOpenClawCancelRequest = (input) => buildAdapterCancelRequest({
    reason: input.reason,
    taskId: input.taskId,
});
const resolveDefaultOpenClawInvoke = (options) => {
    const openClawTimeoutMs = options.openClawTimeoutMs ?? resolveOpenClawTimeoutMsLocal();
    return async (input) => {
        const launchContract = await resolveOpenClawDispatchLaunchContract({
            inputLaunchContract: input.launchContract,
            openClawBin: options.openClawBin,
            optionLaunchContract: options.launchContract,
        });
        const openClawWorkdir = input.runtimeContext.providerRuntimeWorkingDirectory;
        const onProcessControlEvent = resolveRuntimeProcessRegistrar({
            registerRuntimeProcess: options.registerRuntimeProcess,
            taskId: input.taskId,
        });
        const openClawAgentId = resolveOpenClawAgentId({
            dispatchEnvAgentId: input.env?.WAKELOOP_DAEMON_RUNTIME_OPENCLAW_AGENT_ID,
            optionAgentId: options.openClawAgentId,
        });
        const sessionKey = resolveOpenClawSessionKey(input);
        if (sessionKey) {
            return await invokeOpenClawGatewayPath({
                input,
                launchContract,
                openClawAgentId,
                openClawTimeoutMs,
                openClawWorkdir,
                sessionKey,
            });
        }
        return await invokeOpenClawLocalCliPath({
            input,
            launchContract,
            onProcessControlEvent,
            openClawAgentId,
            openClawTimeoutMs,
            openClawWorkdir,
        });
    };
};
async function resolveOpenClawDispatchLaunchContract(input) {
    const explicitLaunchContract = input.inputLaunchContract ??
        input.optionLaunchContract ??
        (normalizeOptionalText(input.openClawBin)
            ? buildExecutableLaunchContract({
                commandPath: normalizeOptionalText(input.openClawBin),
            })
            : null);
    if (explicitLaunchContract) {
        const verification = await verifyOpenClawLaunchContractLocal({
            launchContract: explicitLaunchContract,
        });
        if (verification.ok) {
            return explicitLaunchContract;
        }
        throw Object.assign(new Error(verification.errorMessage), {
            code: "controller.launch.needs_repair",
            errorType: "config",
        });
    }
    throw Object.assign(new Error("OpenClaw launch contract is required for runtime dispatch."), {
        code: "controller.launch.needs_repair",
        errorType: "config",
    });
}
const resolveRuntimeProcessRegistrar = (input) => input.registerRuntimeProcess
    ? (event) => {
        input.registerRuntimeProcess?.({
            ...event,
            taskId: input.taskId,
        });
    }
    : undefined;
const resolveOpenClawAgentId = (input) => normalizeOptionalText(input.dispatchEnvAgentId) ??
    normalizeOptionalText(input.optionAgentId) ??
    resolveOpenClawAgentIdLocal();
const resolveOpenClawSessionKey = (input) => normalizeOptionalText(input.agentControllerConversation?.agentControllerRef ===
    "builtin:openclaw" &&
    input.agentControllerConversation.agentControllerConversationKind ===
        "session"
    ? input.agentControllerConversation.agentControllerConversationId
    : null);
const invokeOpenClawGatewayPath = async (input) => {
    const gatewayResult = await invokeOpenClawGatewayTransport({
        agentId: input.openClawAgentId,
        env: input.input.env,
        idempotencyKey: input.input.dispatchId ?? input.input.taskId,
        launchContract: input.launchContract,
        openClawTimeoutMs: input.openClawTimeoutMs,
        openClawWorkdir: input.openClawWorkdir,
        prompt: input.input.prompt,
        resumedContext: normalizeOptionalText(input.input.agentControllerConversationId) !== null,
        sessionKey: input.sessionKey,
    });
    return {
        contextDelta: {
            agentControllerConversationId: gatewayResult.sessionId,
            agentControllerConversation: buildAdapterAgentControllerConversationRef({
                agentControllerRef: "builtin:openclaw",
                agentControllerConversationId: gatewayResult.sessionKey,
                agentControllerConversationKind: "session",
            }),
        },
        output: gatewayResult.output,
        ...(gatewayResult.telemetry ? { telemetry: gatewayResult.telemetry } : {}),
    };
};
const invokeOpenClawLocalCliPath = async (input) => {
    const result = await invokeOpenClawWithSingleContextHealLocal({
        launchContract: input.launchContract,
        openClawAgentId: input.openClawAgentId,
        openClawTimeoutMs: input.openClawTimeoutMs,
        openClawWorkdir: input.openClawWorkdir,
        env: input.input.env,
        executionMode: input.input.executionMode,
        ...(input.onProcessControlEvent
            ? { onProcessControlEvent: input.onProcessControlEvent }
            : {}),
        ...(input.input.onRuntimeEvent
            ? { onRuntimeEvent: input.input.onRuntimeEvent }
            : {}),
        prompt: input.input.prompt,
        sessionId: normalizeOptionalText(input.input.agentControllerConversationId),
    });
    const agentControllerConversation = OPENCLAW_CONVERSATION_BRIDGE_SPEC
        ? bridgeAgentControllerConversationRef({
            bridgeSpec: OPENCLAW_CONVERSATION_BRIDGE_SPEC,
            agentControllerConversationId: result.sessionId,
        })
        : null;
    return {
        contextDelta: {
            agentControllerConversationId: result.sessionId,
            ...(result.contextHealed ? { refreshed: true } : {}),
            ...(agentControllerConversation ? { agentControllerConversation } : {}),
        },
        output: result.output,
        ...(result.telemetry ? { telemetry: result.telemetry } : {}),
    };
};
function normalizeText(value) {
    return String(value ?? "").trim();
}
function normalizeOptionalText(value) {
    const normalized = normalizeText(value);
    return normalized.length > 0 ? normalized : null;
}
